'use server';
import { actionErrorMessage } from '@/shared/infrastructure/logging/actionError';
import type { ActionResult } from '@/shared/types/actions';
import { SessionService } from '@/contexts/authentication';
import { walletAccountId } from '@/contexts/subscriptions/services/walletAccountId';

/** Character fields the LLM can pull from a spoken description (all optional). */
export interface ExtractedCharacter {
  name?: string;
  physicalDescription?: string;
  personalityTraits?: string;
  specialAbilities?: string;
  gender?: string;
  age?: string;
}

const SYSTEM = [
  'You turn a spoken description of a story character (from a parent or a young child) into a compact JSON object.',
  'The speech may ramble or mix languages — infer charitably; keep the text in the SAME language they spoke.',
  'Return ONLY a JSON object with any of these optional keys (omit what is not said):',
  '  name: string',
  '  physicalDescription: string — how they look.',
  '  personalityTraits: string — their character/vibe.',
  '  specialAbilities: string — powers or special things they can do.',
  '  gender: "male" | "female" | "neutral".',
  '  age: string — e.g. "6" or "young".',
].join('\n');

function coerce(raw: unknown): ExtractedCharacter | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const str = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : undefined);
  const out: ExtractedCharacter = {
    name: str(o.name),
    physicalDescription: str(o.physicalDescription),
    personalityTraits: str(o.personalityTraits),
    specialAbilities: str(o.specialAbilities),
    gender: str(o.gender),
    age: str(o.age),
  };
  return Object.values(out).some(Boolean) ? out : null;
}

export async function extractCharacterFromDictation(
  transcript: string,
  locale: string,
): Promise<ActionResult<ExtractedCharacter>> {
  try {
    const familyId = await SessionService.getFamilyId();
    const userId = await SessionService.getUserId();
    if (!familyId || !userId) return { success: false, error: 'Not authenticated' };
    const text = (transcript || '').trim();
    if (text.length < 3) return { success: false, error: 'Empty transcript' };

    const { UnifiedAIConfigService } = await import('@/contexts/ai/services/config/UnifiedAIConfigService');
    const { getTokenManager } = await import('@/contexts/subscriptions/services/getTokenManager');
    const { buildLlmMessagesAndParams, toWorkerLlmProvider } = await import('@/contexts/ai/services/oneShotTask');
    const { runLlmGenerate } = await import('@/shared/infrastructure/workflows/llmRun');
    const { billedFailuresOf, trackBilledFailuresCovered } = await import('@/shared/infrastructure/workflows/billedFailures');

    const estimatedCost = await UnifiedAIConfigService.getOperationReservation('block_regeneration');
    const tokenManager = getTokenManager();
    const reservation = await tokenManager.reserve({
      familyId,
      accountId: await walletAccountId(familyId),
      amount: estimatedCost,
      operationType: 'character_dictation_extract',
      metadata: { createdBy: userId },
    });

    try {
      const model = await UnifiedAIConfigService.getModelForCategory('llm');
      const provider = toWorkerLlmProvider((await UnifiedAIConfigService.getConfig()).llm?.provider);
      const { messages, params } = buildLlmMessagesAndParams({
        system: SYSTEM,
        user: `Language: ${locale}\nDictation:\n"""${text}"""`,
        model,
        maxTokens: 500,
      });
      const out = await runLlmGenerate({
        startKey: `character-extract-${familyId}-${Date.now()}`,
        messages,
        params: { ...params, response_format: { type: 'json_object' } },
        model,
        provider,
        familyId,
      });

      // Settle through the price-book (raw LLM usage → accounts prices → cost_facts +
      // correct cost-based credits), NOT legacy token math. Best-effort: a billing
      // hiccup must not discard a good extraction (TTL sweep refunds a leaked hold).
      const promptTokens = Number(out.usage?.inputTokens ?? 0);
      const completionTokens = Number(out.usage?.outputTokens ?? 0);
      try {
        // charged provider failures inside the run (a Gemini block) → covered COGS, before settle
        await trackBilledFailuresCovered(tokenManager, reservation.id, out.billedFailures);
        await tokenManager.trackOperation({
          reservationId: reservation.id,
          type: 'text_generation',
          llmUsage: { promptTokens, completionTokens, totalTokens: promptTokens + completionTokens, model },
        });
        await tokenManager.settle({ reservationId: reservation.id });
      } catch {
        /* best-effort billing; leaked hold is TTL-swept */
      }

      let parsed: unknown;
      try {
        parsed = JSON.parse(out.text);
      } catch {
        const m = out.text.match(/\{[\s\S]*\}/);
        parsed = m ? JSON.parse(m[0]) : null;
      }
      const fields = coerce(parsed);
      if (!fields) return { success: false, error: 'Could not extract character' };
      return { success: true, data: fields };
    } catch (e) {
      await trackBilledFailuresCovered(tokenManager, reservation.id, billedFailuresOf(e));
      await tokenManager.refund({ reservationId: reservation.id, reason: `character extract failed: ${e}` }).catch(() => {});
      throw e;
    }
  } catch (e: unknown) {
    return { success: false, error: actionErrorMessage('extractCharacterFromDictation', e) };
  }
}
